import {useUser} from '../use-user';
import {Trans} from '../../../i18n/trans';
import {useState} from 'react';
import {apiClient} from '../../../http/query-client';
import {toast} from '../../../ui/toast/toast';
import mailSentSvg from './mail-sent.svg';
import {SvgImage} from '../../../ui/images/svg-image/svg-image';
import {Button} from '../../../ui/buttons/button';
import {useResendVerificationEmail} from '../../requests/use-resend-verification-email';
import {useIsDarkMode} from '../../../ui/themes/use-is-dark-mode';
import {useSettings} from '../../../core/settings/use-settings';

export function EmailVerificationPage() {
  const {data} = useUser('me');
  const resendEmail = useResendVerificationEmail();
  const {
    branding: {logo_light, logo_dark},
  } = useSettings();
  const isDarkMode = useIsDarkMode();
  const logoSrc = isDarkMode ? logo_light : logo_dark;

  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerify = async () => {
    if (!otp || otp.length !== 6) {
      toast.danger('Please enter a valid 6-digit code.');
      return;
    }
    
    setIsVerifying(true);
    try {
      const response = await apiClient.post('auth/email/verify-otp', {
        email: data?.user.email,
        code: otp,
      });
      if (response.data.status === 'success') {
        toast.positive('Email verified successfully!');
        window.location.href = '/login?verified=1';
      }
    } catch (e: any) {
      toast.danger(e?.response?.data?.message || 'Invalid or expired code.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="flex flex-col items-center p-24 bg-alt w-full h-full">
      {logoSrc && (
        <img
          src={logoSrc}
          alt="Site logo"
          className="my-60 block h-42 w-auto"
        />
      )}
      <div className="bg-paper px-14 py-28 rounded shadow border max-w-580 flex flex-col items-center text-center">
        <SvgImage src={mailSentSvg} className="h-144" />
        <h1 className="text-3xl mt-40 mb-20">
          <Trans message="Verify your email" />
        </h1>
        <div className="mb-24 text-sm">
          <Trans
            message="We've sent an email to “:email“ to verify your email address. Please enter the 6-digit code below or click the link in the email."
            values={{email: data?.user.email}}
          />
        </div>
        
        <div className="mb-24 w-full flex flex-col items-center">
          <input
            type="text"
            className="w-144 text-center text-2xl tracking-widest border rounded p-10 outline-none focus:ring-2 focus:ring-primary"
            placeholder="000000"
            maxLength={6}
            value={otp}
            onChange={e => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
          />
          <Button
            className="mt-14"
            variant="flat"
            color="primary"
            disabled={isVerifying || otp.length !== 6}
            onClick={handleVerify}
          >
            <Trans message="Verify Code" />
          </Button>
        </div>

        <div className="text-sm">
          <Trans message="If you did not receive an email, click the button below and we will send you another one." />
        </div>
        <Button
          className="mt-30"
          variant="flat"
          color="primary"
          disabled={resendEmail.isLoading || !data?.user.email}
          onClick={() => {
            resendEmail.mutate({email: data!.user.email});
          }}
        >
          <Trans message="Resend email" />
        </Button>
      </div>
    </div>
  );
}
